import { forwardRef, lazy, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useFieldArray, useWatch } from "react-hook-form";
import {
  DatePicker,
  Dropdown,
  FilePicker,
  GroupRenderer,
  Input,
  Textarea,
} from "stories";
import { FilePreview } from "stories/components/filePicker/FilePicker";
import { RadioGroupList } from "stories/components/radioGroup/RadioGroup";
import { ConditionalFormRenderer } from "../../../../../../../stories/components/FormRenderer/ConditionalFormRenderer";
import { staticEntities } from "../constants";
import { getEventItemModel } from "../eventChart/EventItem";

const EventChart = lazy(() => import("../eventChart/EventChart"));

export const marketStrategyFormFields = [
  [
    {
      renderAsFormItem: false,
      component: () => (
        <div className="section-title fw-bold mb-4">
          Sales & Marketing Program Schedule
        </div>
      ),
      props: {},
    },
  ],
  [
    {
      name: `gantt_chart`,
      component: RadioGroupList,
      props: {
        label: "Do you have a Gantt Chart?",
        list: staticEntities.booleanList,
      },
    },
    {},
  ],
  [
    {
      renderAsFormItem: false,
      component: ConditionalFormRenderer,
      props: {
        conditionFieldName: `gantt_chart`,
        conditionalFields: {
          true: [
            [
              {
                name: `filename`,
                component: FilePicker,
                props: {
                  label: "Gantt Chart",
                  type: "file",
                },
              },
              {},
            ],
          ],
          false: [
            [
              {
                renderAsFormItem: false,
                component: EventChart,
                props: {},
              },
            ],
            [
              {
                name: `events`,
                renderAsFormItem: false,
                component: GroupRenderer,
                props: {
                  title: "Events",
                  itemPrefix: "Event",
                  addButtonText: "Add Event",
                  fields: [
                    [
                      {
                        name: "name",
                        component: Input,
                        props: {
                          label: "Name",
                        },
                      },
                      {},
                    ],
                    [
                      {
                        name: "start_date",
                        component: DatePicker,
                        props: {
                          label: "Start Date",
                        },
                      },
                      {
                        name: "end_date",
                        component: DatePicker,
                        props: {
                          label: "End Date",
                        },
                      },
                    ],
                    [
                      {
                        name: "status",
                        component: Dropdown,
                        props: {
                          label: "Status",
                          list: staticEntities.eventStatusList,
                        },
                      },
                      {},
                    ],
                  ],
                  getModelItem: getEventItemModel,
                },
              },
            ],
          ],
        },
      },
    },
  ],
  [
    {
      name: "team",
      component: FilePicker,
      props: {
        label: "Sales & Marketing Team",
        type: "file",
      },
    },
    {
      name: "strategy_description",
      component: Textarea,
      props: {
        label: "Marketing Strategy Description",
      },
    },
  ],
  [
    {
      renderAsFormItem: false,
      component: () => (
        <div className="section-title fw-bold mb-4">
          Sales & Marketing Presale Reports
        </div>
      ),
      props: {},
    },
  ],
  [
    {
      name: "report_files",
      component: FilePicker,
      props: {
        label: "Reports",
        type: "file",
        showSelectedFiles: false,
        multiple: true,
        maxFilesCount: 0,
      },
    },
    {},
  ],
  [
    {
      name: "reports",
      component: forwardRef(function FormFileDate({ form }, ref) {
        const { control } = form || {};
        const reportFiles = useWatch({
          name: "report_files",
          control: control,
        });
        const {
          fields: files,
          append,
          update,
          remove,
        } = useFieldArray({
          name: "reports",
          control: control,
          keyName: "key",
        });

        useEffect(() => {
          if ((reportFiles || []).length > (files || []).length) {
            for (let i = 0; i < reportFiles.length; i++) {
              if (!files[i] || reportFiles[i] !== files[i].file) {
                append({
                  file: reportFiles[i],
                  date: null,
                });
              }
            }
          }
        }, [reportFiles]); // eslint-disable-line

        const handleOnDelete = (index) => {
          files.splice(index, 1);
          form.setValue(
            "report_files",
            files.map((fileRow) => fileRow.file)
          );
          remove(index);
        };

        if (!form || !files || !files.length) return <></>;
        return (
          <div ref={ref}>
            <span className="label-text mb-3 mt-0 d-block">Attachment:</span>
            {files.map((fileRow, i) => {
              return (
                <Row key={fileRow.key}>
                  <Col className="overflow-hidden">
                    <FilePreview
                      file={fileRow.file}
                      onDelete={handleOnDelete.bind(null, i)}
                    />
                  </Col>
                  <Col>
                    <DatePicker
                      label="Report Date"
                      value={fileRow.date}
                      onChange={(e) =>
                        update(i, { ...fileRow, date: e.target.value })
                      }
                    />
                  </Col>
                </Row>
              );
            })}
          </div>
        );
      }),
      props: (form) => ({ form }),
    },
  ],
  [
    {
      name: "purchase_deposit_rate_amount",
      component: Input,
      props: {
        label: "Purchase Deposit Rate",
        type: "currency",
        startAdornment: "$",
      },
    },
    {},
  ],
];
